import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const Menu = ({ anchorEl, open, onClose, children, className = "", placement = "bottom", matchAnchorWidth = false }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!open || !anchorEl) return undefined;
    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const top = placement === "top" ? rect.top + window.scrollY : rect.bottom + window.scrollY;
      setPosition({ top, left: rect.left + window.scrollX, width: matchAnchorWidth ? rect.width : undefined });
    };
    updatePosition();
    // O menu fica no body (portal): se a âncora estiver dentro de um container
    // que rola (ex.: sidebar), acompanha a rolagem em vez de ficar parado.
    // `capture` pega o scroll de qualquer ancestral, não só o da janela.
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, anchorEl, placement, matchAnchorWidth]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target) && event.target !== anchorEl) {
        onClose?.(event, "backdropClick");
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.(event, "escapeKeyDown");
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, anchorEl]);

  if (!open || !position) return null;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        width: position.width,
        transform: placement === "top" ? "translateY(calc(-100% - 4px))" : undefined,
      }}
      // Acima de qualquer Modal (z-[1300]–[1600]): o menu vai pro body, mesmo nível do Form/Select
      className={`z-[9000] min-w-[160px] rounded-lg border border-farmazon-border bg-farmazon-surface py-1 shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body
  );
};

export const MenuItem = ({ className = "", disabled = false, onClick, children, ...rest }) => (
  <div
    role="menuitem"
    onClick={disabled ? undefined : onClick}
    className={`flex cursor-pointer items-center gap-2 px-4 py-2 text-sm text-farmazon-text hover:bg-farmazon-hover ${
      disabled ? "cursor-not-allowed opacity-50" : ""
    } ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export default Menu;
